<template>
  <div class="nlq-sec">
    <!-- Accordion tab strip -->
    <div class="nlq-tabs">
      <div
        v-for="(tab, i) in visibleTabs"
        :key="tab.key"
        class="nlq-tab"
        :class="{ 'is-locked': isTabLocked(tab) }"
        :title="isTabLocked(tab) ? 'Locked Section' : ''"
        @click="toggle(i)"
      >
        <div
          class="nlq-tab-header"
          :class="{ 'nlq-tab-header--locked': isTabLocked(tab) }"
          :style="{ background: isTabLocked(tab) ? '#bbbdbf' : (activeIndex === i ? tab.activeColor : '#bbbdbf') }"
        >
          <SectionLockBadge v-if="isTabLocked(tab)" size="tab" />
          <div class="nlq-tab-main">
            <div class="nlq-tab-label">
              <img
                v-if="tab.icon"
                :src="tab.icon"
                alt=""
                class="nlq-tab-leading-icon"
                :class="{ 'nlq-tab-leading-icon--journal': tab.key === 'journal' || tab.key === 'comments' }"
              />
              <h4>{{ tabLabel(tab) }}</h4>
            </div>
            <div v-if="!isTabLocked(tab)" class="nlq-tab-icon">
              <svg v-if="activeIndex === i" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19" stroke="#fff" stroke-width="2" stroke-linecap="round" />
              </svg>
              <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 5V19" stroke="#fff" stroke-width="2" stroke-linecap="round" />
                <path d="M5 12H19" stroke="#fff" stroke-width="2" stroke-linecap="round" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sliding content panel -->
    <transition name="nlq-slide">
      <div
        v-if="activeIndex !== null"
        class="nlq-panel"
        :style="{
          borderColor: tabColor(activeTabKey),
          borderTopColor: tabColor(activeTabKey),
        }"
      >
        <!-- ── Questions tab ── -->
        <div v-if="activeTabKey === 'questions'">
          <div class="nlq-birdeye-card" :style="{ borderColor: tabColor('questions') }">
            <div class="nlq-birdeye-title" :style="{ background: tabColor('questions') }">
              {{ panelHeaderLabel('questions') }}
            </div>

            <div v-if="questionsLoading" class="nlq-state">Loading questions…</div>

            <div v-else-if="questions.length === 0" class="nlq-state">
              {{ emptyQuestionsMessage }}
            </div>

            <div v-else>
              <div class="nlq-birdeye-grid">
                <div
                  v-for="(que, index) in questions"
                  :key="que.id"
                  class="nlq-birdeye-box"
                  :style="{ background: boxColor(que) }"
                  @click="openPreview('question', que)"
                  title="Click to view this question"
                >
                  <span>{{ displayNumber(que, index, 'question') }}</span>
                </div>
              </div>
              <div class="nlq-legend">
                <span class="nlq-legend-item">
                  <span class="nlq-legend-dot" style="background:#9ded6c;"></span> Correct
                </span>
                <span class="nlq-legend-item">
                  <span class="nlq-legend-dot" style="background:#ffbabe;"></span> Incorrect
                </span>
                <span class="nlq-legend-item">
                  <span class="nlq-legend-dot" style="background:#f1f2f2; border:1px solid #ccc;"></span> Not attempted
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Mocks tab (notes page only) ── -->
        <div v-else-if="activeTabKey === 'mocks'">
          <div class="nlq-birdeye-card" :style="{ borderColor: tabColor('mocks') }">
            <div class="nlq-birdeye-title" :style="{ background: tabColor('mocks') }">
              {{ panelHeaderLabel('mocks') }}
            </div>

            <div v-if="mocksLoading" class="nlq-state">Loading mocks…</div>

            <div v-else-if="mockQuestions.length === 0" class="nlq-state">
              {{ emptyMocksMessage }}
            </div>

            <div v-else>
              <div class="nlq-birdeye-grid">
                <div
                  v-for="(que, index) in mockQuestions"
                  :key="que.id"
                  class="nlq-birdeye-box"
                  :style="{ background: boxColor(que) }"
                  @click="openPreview('mock', que)"
                  title="Click to view this mock question"
                >
                  <span>{{ displayNumber(que, index, 'mock') }}</span>
                </div>
              </div>
              <div class="nlq-legend">
                <span class="nlq-legend-item">
                  <span class="nlq-legend-dot" style="background:#9ded6c;"></span> Correct
                </span>
                <span class="nlq-legend-item">
                  <span class="nlq-legend-dot" style="background:#ffbabe;"></span> Incorrect
                </span>
                <span class="nlq-legend-item">
                  <span class="nlq-legend-dot" style="background:#f1f2f2; border:1px solid #ccc;"></span> Not attempted
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Notes tab (question / mock pages) ── -->
        <div v-else-if="activeTabKey === 'linked-notes'">
          <div class="nlq-birdeye-card" :style="{ borderColor: tabColor('linked-notes') }">
            <div class="nlq-birdeye-title" :style="{ background: tabColor('linked-notes') }">
              {{ panelHeaderLabel('linked-notes') }}
            </div>

            <div v-if="notesLoading" class="nlq-state">Loading notes…</div>

            <div v-else-if="linkedNotes.length === 0" class="nlq-state">
              No notes are linked.
            </div>

            <div v-else>
              <div class="nlq-birdeye-grid">
                <div
                  v-for="(note, index) in linkedNotes"
                  :key="note.record_id + '-' + note.type"
                  class="nlq-birdeye-box nlq-birdeye-box--note"
                  @click="openPreview('note', note)"
                  :title="note.label || note.display_code"
                >
                  <span>{{ displayNumber(note, index, 'note') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Journal tab ── -->
        <div v-else-if="activeTabKey === 'journal'" class="nlq-journal-panel">
          <div class="nlq-birdeye-card" :style="{ borderColor: tabColor('journal') }">
            <div class="nlq-birdeye-title" :style="{ background: tabColor('journal') }">
              {{ panelHeaderLabel('journal') }}
            </div>
            <div class="nlq-tab-body">
              <div v-if="!noteRecordId" class="nlq-state">
                <p>{{ journalEmptyHint }}</p>
              </div>
              <template v-else>
                <p class="nlq-journal-hint">Your journal is private to your account and stored in your Synapse profile.</p>
                <div v-if="journalLoading" class="nlq-state">Loading journal…</div>
                <div v-else class="nlq-journal-editor-wrap">
                  <NotesEditor v-model="journalHtml" />
                </div>
                <div v-if="!journalLoading" class="nlq-journal-actions">
                  <button type="button" class="nlq-journal-save" @click="saveJournal">Save</button>
                  <span v-if="journalSaveMessage" class="nlq-journal-saved-msg">{{ journalSaveMessage }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- ── Comments tab ── -->
        <div v-else-if="activeTabKey === 'comments'" class="nlq-comments-panel">
          <div class="nlq-birdeye-card" :style="{ borderColor: tabColor('comments') }">
            <div class="nlq-birdeye-title" :style="{ background: tabColor('comments') }">
              {{ panelHeaderLabel('comments') }}
            </div>
            <div class="nlq-tab-body">
              <div v-if="!canUseComments" class="nlq-state">
                <p>{{ commentsUnavailableMessage }}</p>
              </div>
              <template v-else>
                <p v-if="commentsLoading" class="nlq-state">Loading comments…</p>
                <ul v-else-if="commentList.length" class="nlq-comments-list">
                  <li v-for="comment in commentList" :key="comment.id" class="nlq-comment-item">
                    <div class="nlq-user-avatar">
                      <img
                        src="https://static.vecteezy.com/system/resources/previews/047/733/682/non_2x/grey-avatar-icon-user-avatar-photo-icon-social-media-user-icon-vector.jpg"
                        alt=""
                      />
                    </div>
                    <div class="nlq-comment-content">
                      <div class="nlq-comment-header">
                        <span class="nlq-user-name">{{ commentUserName(comment) }}</span>
                      </div>
                      <p class="nlq-comment-text">{{ comment.comments || 'No details' }}</p>
                    </div>
                  </li>
                </ul>
                <p v-else class="nlq-state">No comments yet. Be the first to comment.</p>
                <div class="nlq-comment-input-container">
                  <input
                    v-model="commentDraft"
                    type="text"
                    placeholder="Write your comment…"
                    @keyup.enter="saveComment"
                  />
                  <button type="button" class="nlq-send-button" :disabled="commentSaving" @click="saveComment">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="#ED1C24" />
                    </svg>
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="nlq-modal-fade">
      <div
        v-if="previewOpen"
        ref="previewOverlay"
        class="nlq-preview-overlay"
        @click.self="closePreview"
      >
        <div class="nlq-preview-modal" role="dialog" aria-modal="true">
          <div class="nlq-preview-header" :style="{ borderColor: previewColor }">
            <div>
              <p class="nlq-preview-type">{{ previewTypeLabel }}</p>
              <h3>{{ previewTitle }}</h3>
            </div>
            <button type="button" class="nlq-preview-close" aria-label="Close" @click="closePreview">
              <svg width="14" height="14" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.38013 0.75L8.1701 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
                <path d="M8.1701 0.75L1.38013 7.54001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <!-- <div class="nlq-preview-frame-wrap">
  <iframe
    :src="previewSrc"
    class="nlq-preview-frame"
    :title="previewTitle"
  ></iframe>

  <div
    class="nlq-preview-click-blocker"
    @click.prevent
    @mousedown.prevent
    @mouseup.prevent
  ></div>
</div> -->

          <div class="nlq-preview-frame-wrap">
            <div v-if="previewError" class="nlq-state">{{ previewError }}</div>
            <iframe
  ref="previewFrame"
  :src="previewSrc"
  class="nlq-preview-frame"
  :title="previewTitle"

></iframe>
            <!-- <iframe
              v-else-if="previewSrc"
              :src="previewSrc"
              class="nlq-preview-frame"
              :title="previewTitle"
            ></iframe> -->
          </div>
        </div>
      </div>
    </transition>

    <SectionSubscribeModal
      :open="showSubscribePopup"
      :message="subscribeMessage"
      @close="showSubscribePopup = false"
    />
  </div>
</template>

<script>
import NotesEditor from '@/components/NotesEditor.vue';
import SectionLockBadge from '@/components/SectionLockBadge.vue';
import SectionSubscribeModal from '@/components/SectionSubscribeModal.vue';
import { fetchJournalHtml, saveJournalHtml } from '@/views/lib/notesUserPrefs';
import { byMethod, get } from '@/views/lib/api';
import { onEmbeddedPreviewCloseRequest } from '@/views/lib/embeddedPreview';
import { lockBodyScrollInline, releaseBodyScrollLock } from '@/views/lib/bodyScrollLock';
import { emptySectionLocks, fetchSectionLocks, subscribeSectionMessage } from '@/views/lib/sectionLocks';

export default {
  name: 'NotesLinkedQuestions',
  components: { NotesEditor, SectionLockBadge, SectionSubscribeModal },
  props: {
    questions:        { type: Array,   default: () => [] },
    questionsLoading: { type: Boolean, default: false },
    mockQuestions:    { type: Array,   default: () => [] },
    mocksLoading:     { type: Boolean, default: false },
    linkedNotes:      { type: Array,   default: () => [] },
    notesLoading:     { type: Boolean, default: false },
    title:            { type: String,  default: '' },
    /** Shown in Questions tab (links variant) — condition name. */
    conditionTitle:   { type: String,  default: '' },
    /** Shown in Notes tab header when available. */
    noteName:         { type: String,  default: '' },
    /** Shown in Mocks tab on note pages (e.g. ACP001). */
    noteCode:         { type: String,  default: '' },
    noteRecordId:     { type: String,  default: '' },
    noteRecordType:   { type: String,  default: 'notes' },
    /** notes = full note page; links = question/mock page (4 tabs, no mocks tab) */
    variant:          { type: String,  default: 'notes' },
    currentQuestion:  { type: Object,  default: null },
    /** subject → /addcomment; mock → /addmockcomment; note → /note-comments */
    commentApi:       { type: String,  default: 'subject' },
  },
  data() {
    return {
      activeIndex: null,
      journalHtml: '',
      journalLoading: false,
      journalSaveMessage: '',
      journalSaveTimer: null,
      commentDraft: '',
      commentSaving: false,
      noteComments: [],
      commentsLoading: false,
      previewOpen: false,
      previewKind: '',
      previewItem: null,
      previewSrc: '',
      previewError: '',
      sectionLocks: emptySectionLocks(),
      showSubscribePopup: false,
      subscribeMessage: '',
      tabDefs: {
        questions:       { label: 'Questions', activeColor: '#00A2FD', icon: '/images/questionmark.png' },
        mocks:           { label: 'Mocks',     activeColor: '#ED1C24', icon: '/images/brain.png' },
        'linked-notes':  { label: 'Notes',     activeColor: '#FBAD18', icon: '/images/file.png' },
        // Journal / Comments stay grey whether expanded or collapsed
        journal:         { label: 'Journal',   activeColor: '#A7A9AC', icon: '/images/journal-icon.png' },
        comments:        { label: 'Comments',  activeColor: '#A7A9AC', icon: '/images/comments-icon.svg' },
      },
    };
  },
  computed: {
    visibleTabs() {
      // Notes page: hide Notes tab (you're already on a note). Q/M pages keep all five.
      const tabs = [
        { key: 'questions', ...this.tabDefs.questions },
        { key: 'mocks', ...this.tabDefs.mocks },
        { key: 'journal', ...this.tabDefs.journal },
        { key: 'comments', ...this.tabDefs.comments },
      ];
      if (this.variant !== 'notes') {
        tabs.splice(1, 0, { key: 'linked-notes', ...this.tabDefs['linked-notes'] });
      }
      return tabs;
    },
    isEmbeddedPreview() {
      return !!(this.$route && this.$route.query && this.$route.query.embeddedPreview === '1');
    },
    emptyQuestionsMessage() {
      return this.variant === 'links'
        ? 'No questions are linked.'
        : 'No questions are linked to this note.';
    },
    emptyMocksMessage() {
      return this.variant === 'links'
        ? 'No mocks are linked.'
        : 'No mock questions are linked to this note.';
    },
    journalEmptyHint() {
      if (this.variant === 'links') {
        return 'Open a question to use the journal.';
      }
      return 'Open a note or subnote to use the journal.';
    },
    commentList() {
      if (this.isNoteCommentsMode) {
        return Array.isArray(this.noteComments) ? this.noteComments : [];
      }
      const q = this.currentQuestion;
      if (!q || !Array.isArray(q.comments)) return [];
      return q.comments;
    },
    isNoteCommentsMode() {
      return this.commentApi === 'note' || this.variant === 'notes';
    },
    canUseComments() {
      if (this.isNoteCommentsMode) {
        return !!(this.noteRecordId);
      }
      return !!(this.currentQuestion && this.currentQuestion.id);
    },
    commentsUnavailableMessage() {
      if (this.isNoteCommentsMode) {
        return 'Open a note to view or add comments.';
      }
      return 'Select a question to view or add comments.';
    },
    activeTabKey() {
      if (this.activeIndex === null) return null;
      const tab = this.visibleTabs[this.activeIndex];
      return tab ? tab.key : null;
    },
    previewColor() {
      if (this.previewKind === 'note') return this.tabColor('linked-notes');
      if (this.previewKind === 'mock') return this.tabColor('mocks');
      return this.tabColor('questions');
    },
    previewTypeLabel() {
      if (this.previewKind === 'note') return 'Note';
      if (this.previewKind === 'mock') return 'Mock';
      return 'Question';
    },
    previewTitle() {
      const item = this.previewItem || {};
      if (this.previewKind === 'note') {
        return item.label || item.display_code || item.title || 'Linked note';
      }
      return item.number || item.question_no || item.code || item.display_code || 'Linked question';
    },
    /** Questions tab → condition on question pages; note name on note pages. */
    questionsHeaderLabel() {
      const fromProp = (this.conditionTitle || '').trim();
      if (fromProp) return fromProp;
      const q = this.currentQuestion;
      const fromRelation = q && q.condition && q.condition.title
        ? String(q.condition.title).trim()
        : '';
      if (fromRelation) return fromRelation;
      if (q && q.condition_title) return String(q.condition_title).trim();
      if (q && q.context_label) return String(q.context_label).trim();
      if (q && q.linked_preview_topic) return String(q.linked_preview_topic).trim();
      if (q && q.sublist && q.sublist.title) return String(q.sublist.title).trim();
      if (q && q.subject && q.subject.title) return String(q.subject.title).trim();
      return '';
    },
    /** Notes tab → note name/title. */
    notesTabHeaderLabel() {
      const fromProp = (this.noteName || '').trim();
      if (fromProp) return fromProp;

      const first = (this.linkedNotes || [])[0];
      if (first) {
        const label = (
          first.label
          || first.title
          || first.display_code
          || ''
        ).trim();
        if (label) return label;
      }

      const topic = (this.conditionTitle || '').trim();
      if (topic) return topic;

      return '';
    },
    /** Mocks tab → question/mock code (question pages only). */
    mocksHeaderLabel() {
      return this.codeHeaderLabel;
    },
    /** Journal / Comments headers → question code / number. */
    codeHeaderLabel() {
      const q = this.currentQuestion;
      if (q) {
        const code = q.number != null && String(q.number).trim() !== ''
          ? String(q.number).trim()
          : (q.code != null ? String(q.code).trim() : '');
        if (code) return code;
      }
      return this.title || '';
    },
  },
  watch: {
    noteRecordId: {
      handler() {
        this.loadJournalFromPrefs();
        this.loadNoteComments();
      },
      immediate: true,
    },
    noteRecordType() {
      this.loadJournalFromPrefs();
      this.loadNoteComments();
    },
    activeIndex() {
      const key = this.activeTabKey;
      if (key === 'journal' && this.noteRecordId) {
        this.loadJournalFromPrefs();
      }
      if (key === 'comments' && this.isNoteCommentsMode) {
        this.loadNoteComments();
      }
    },
    currentQuestion: {
      handler() {
        if (!this.isNoteCommentsMode) {
          // Ensure comments array exists for Vue reactivity when appending
          const q = this.currentQuestion;
          if (q && !Array.isArray(q.comments)) {
            this.$set(q, 'comments', []);
          }
        }
      },
      immediate: true,
    },
    previewOpen(isOpen) {
      if (isOpen) {
        document.body.classList.add('nlq-preview-active');
        lockBodyScrollInline();
        this.$nextTick(() => this.attachPreviewOverlayToBody());
      } else {
        document.body.classList.remove('nlq-preview-active');
        releaseBodyScrollLock();
      }
    },
    '$route.query.openTab'() {
      this.applyOpenTabFromRoute();
    },
  },
  mounted() {
    fetchSectionLocks().then((locks) => {
      this.sectionLocks = locks;
      const tab = this.visibleTabs[this.activeIndex];
      if (tab && this.isTabLocked(tab)) {
        this.activeIndex = null;
      }
      this.applyOpenTabFromRoute();
    });
    this.applyOpenTabFromRoute();
    this._embeddedPreviewCloseOff = onEmbeddedPreviewCloseRequest(() => {
      if (this.previewOpen) {
        this.closePreview();
      }
    });
  },
  beforeDestroy() {
    if (this._embeddedPreviewCloseOff) {
      this._embeddedPreviewCloseOff();
      this._embeddedPreviewCloseOff = null;
    }
    if (this.journalSaveTimer) clearTimeout(this.journalSaveTimer);
    document.body.classList.remove('nlq-preview-active');
    releaseBodyScrollLock();
  },
  methods: {
    /**
     * Notes screen → note name in every tab header.
     * Mock review screen → code in every tab header.
     * Question screen → condition in Questions / Notes / Mocks; code in Journal / Comments.
     */
    panelHeaderLabel(tabKey) {
      if (this.variant === 'notes') {
        const name = (this.noteName || this.title || '').trim();
        return name;
      }
      if (this.variant === 'links' && this.noteRecordType === 'mock') {
        return this.codeHeaderLabel;
      }
      if (this.variant === 'links' && this.noteRecordType === 'question') {
        if (tabKey === 'questions' || tabKey === 'linked-notes' || tabKey === 'mocks') {
          return this.questionsHeaderLabel;
        }
        return this.codeHeaderLabel;
      }
      if (tabKey === 'questions') return this.questionsHeaderLabel;
      if (tabKey === 'linked-notes') return this.notesTabHeaderLabel;
      if (tabKey === 'mocks') return this.mocksHeaderLabel;
      return this.codeHeaderLabel;
    },

    attachPreviewOverlayToBody() {
      const el = this.$refs.previewOverlay;
      if (!el || el.parentNode === document.body) return;
      document.body.appendChild(el);
    },

   makeReadonly() {
  const iframe = this.$refs.previewFrame;

  try {
    const doc = iframe.contentDocument || iframe.contentWindow.document;

    // Disable all mouse interactions
    const style = doc.createElement('style');
    style.innerHTML = `
      * {
        pointer-events: none !important;
      }

      body {
        pointer-events: auto !important;
      }
    `;

    doc.head.appendChild(style);
  } catch (e) {
    console.error(e);
  }
},
    recordTypeNorm() {
      return this.noteRecordType === 'subnotes' ? 'subnotes' : 'notes';
    },
    usesLocalJournal() {
      return this.noteRecordType === 'question' || this.noteRecordType === 'mock';
    },
    localJournalStorageKey() {
      if (!this.noteRecordId) return null;
      let uid = 'anon';
      try {
        const u = this.$store && this.$store.getters && this.$store.getters.user;
        if (u && u.id != null) uid = String(u.id);
      } catch (e) { /* ignore */ }
      return `synapse_journal_${this.noteRecordType}_${uid}_${this.noteRecordId}`;
    },
    async loadJournalFromPrefs() {
      if (!this.noteRecordId) {
        this.journalHtml = '';
        return;
      }
      this.journalLoading = true;
      try {
        if (this.usesLocalJournal()) {
          const key = this.localJournalStorageKey();
          this.journalHtml = key ? localStorage.getItem(key) || '' : '';
        } else {
          this.journalHtml = await fetchJournalHtml(
            this.$store,
            this.recordTypeNorm(),
            this.noteRecordId
          );
        }
      } catch (e) {
        this.journalHtml = '';
      } finally {
        this.journalLoading = false;
      }
    },
    async saveJournal() {
      if (!this.noteRecordId) return;
      try {
        if (this.usesLocalJournal()) {
          const key = this.localJournalStorageKey();
          if (key) {
            localStorage.setItem(key, this.journalHtml || '');
            const plain = String(this.journalHtml || '')
              .replace(/<[^>]+>/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();
            if (!plain) {
              localStorage.removeItem(`${key}__meta`);
            } else {
              const q = this.currentQuestion;
              if (q) {
                let meta;
                if (this.noteRecordType === 'mock') {
                  let mockId = q.mock_id || null;
                  let mockTitle = q.mock_title || null;
                  let mockDuration = q.mock_duration != null ? q.mock_duration : null;
                  try {
                    const mo = JSON.parse(localStorage.getItem('mockObject') || 'null');
                    if (mo) {
                      if (mockId == null) mockId = mo.id || null;
                      if (!mockTitle) mockTitle = mo.title || null;
                      if (mockDuration == null) mockDuration = mo.duration != null ? mo.duration : null;
                    }
                  } catch (e) { /* ignore */ }
                  meta = {
                    code: q.number || q.code || q.question_no || null,
                    number: q.number || null,
                    question_no: q.question_no || null,
                    mock_id: mockId,
                    mock_title: mockTitle,
                    mock_duration: mockDuration,
                  };
                } else {
                  meta = {
                    code: q.code || null,
                    subject_id: q.subject_id || null,
                    condition_id: q.condition_id || null,
                    sublist_id: q.sublist_id || null,
                    presentation_id: q.presentation_id || null,
                    condition: (q.condition && (q.condition.title || q.condition)) || q.condition_title || null,
                    subcondition: (q.sublist && (q.sublist.title || q.sublist)) || q.sublist_title || null,
                  };
                }
                localStorage.setItem(`${key}__meta`, JSON.stringify(meta));
              }
            }
          }
        } else {
          await saveJournalHtml(
            this.$store,
            this.recordTypeNorm(),
            this.noteRecordId,
            this.journalHtml
          );
        }
      } catch (e) {
        this.journalSaveMessage = 'Could not save. Try again.';
        return;
      }
      this.journalSaveMessage = 'Saved.';
      if (this.journalSaveTimer) clearTimeout(this.journalSaveTimer);
      this.journalSaveTimer = setTimeout(() => {
        this.journalSaveMessage = '';
        this.journalSaveTimer = null;
      }, 2000);
      this.$emit('journal-updated', {
        id: this.noteRecordId,
        type: this.noteRecordType,
      });
    },
    commentUserName(comment) {
      if (comment && comment.user && comment.user.username) {
        return comment.user.username;
      }
      return 'User';
    },
    appendCommentLocally(comment) {
      if (!comment) return;
      if (this.isNoteCommentsMode) {
        if (!Array.isArray(this.noteComments)) this.noteComments = [];
        if (this.noteComments.some((c) => String(c.id) === String(comment.id))) return;
        this.noteComments = [...this.noteComments, comment];
        return;
      }
      const q = this.currentQuestion;
      if (!q) return;
      if (!Array.isArray(q.comments)) {
        this.$set(q, 'comments', []);
      }
      if (q.comments.some((c) => String(c.id) === String(comment.id))) return;
      q.comments.push(comment);
    },
    async loadNoteComments() {
      if (!this.isNoteCommentsMode || !this.noteRecordId) {
        this.noteComments = [];
        return;
      }
      this.commentsLoading = true;
      try {
        const res = await get('/note-comments', {
          id: this.noteRecordId,
          type: this.noteRecordType === 'subnotes' ? 'subnotes' : 'notes',
        });
        this.noteComments = (res.data && res.data.data) || [];
      } catch (e) {
        console.error(e);
        this.noteComments = [];
      } finally {
        this.commentsLoading = false;
      }
    },
    async saveComment() {
      const text = (this.commentDraft || '').trim();
      if (!text) return;

      if (this.isNoteCommentsMode) {
        if (!this.noteRecordId) return;
        this.commentSaving = true;
        try {
          const res = await byMethod('post', '/note-comments', {
            id: this.noteRecordId,
            type: this.noteRecordType === 'subnotes' ? 'subnotes' : 'notes',
            comments: text,
          });
          if (res.data && res.data.saved) {
            this.commentDraft = '';
            this.appendCommentLocally(res.data.comment);
            this.$emit('comment-saved', {
              kind: 'note',
              recordId: this.noteRecordId,
              comment: res.data.comment,
            });
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.commentSaving = false;
        }
        return;
      }

      const q = this.currentQuestion;
      if (!q || !q.id) return;
      this.commentSaving = true;
      const endpoint = this.commentApi === 'mock' ? '/addmockcomment' : '/addcomment';
      try {
        const res = await byMethod('post', endpoint, {
          question_id: q.id,
          comments: text,
        });
        if (res.data && res.data.saved) {
          this.commentDraft = '';
          this.appendCommentLocally(res.data.comment || {
            id: `tmp-${Date.now()}`,
            comments: text,
            user: { username: 'You' },
          });
          this.$emit('comment-saved', {
            kind: this.commentApi === 'mock' ? 'mock' : 'question',
            questionId: q.id,
            comment: res.data.comment,
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.commentSaving = false;
      }
    },
    toggle(i) {
      const tab = this.visibleTabs[i];
      if (tab && this.isTabLocked(tab)) {
        const section = this.sectionKeyForTab(tab);
        if (section) {
          this.subscribeMessage = subscribeSectionMessage(section);
          this.showSubscribePopup = true;
        }
        return;
      }
      this.activeIndex = this.activeIndex === i ? null : i;
    },
    /** Dashboard Comments / Journals preview → open matching accordion tab. */
    applyOpenTabFromRoute() {
      const raw = this.$route && this.$route.query ? this.$route.query.openTab : null;
      if (!raw) return;
      const key = String(raw).trim().toLowerCase();
      const aliases = {
        comments: 'comments',
        comment: 'comments',
        journal: 'journal',
        journals: 'journal',
      };
      const tabKey = aliases[key] || key;
      const idx = this.visibleTabs.findIndex((t) => t.key === tabKey);
      if (idx < 0) return;
      const tab = this.visibleTabs[idx];
      if (this.isTabLocked(tab)) return;
      this.activeIndex = idx;
      this.$nextTick(() => {
        if (this.$el && typeof this.$el.scrollIntoView === 'function') {
          this.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    },
    sectionKeyForTab(tab) {
      if (!tab) return null;
      if (tab.key === 'questions') return 'questions';
      if (tab.key === 'linked-notes') return 'notes';
      if (tab.key === 'mocks') return 'mocks';
      return null;
    },
    isTabLocked(tab) {
      const section = this.sectionKeyForTab(tab);
      return !!(section && this.sectionLocks && this.sectionLocks[section]);
    },
    /** Close expanded panel (used when changing question in mock review). */
    resetActiveTab() {
      this.activeIndex = null;
    },
    tabColor(key) {
      const def = this.tabDefs[key];
      return def ? def.activeColor : '#00A2FD';
    },
    boxColor(que) {
      if (que.score && que.score.correct == 1) return '#9ded6c';
      if (que.score && que.score.correct == 0) return '#ffbabe';
      return '#f1f2f2';
    },
    displayNumber(item, index, type) {
      if (!item) return index + 1;
      if (type === 'note') {
        return item.display_code || item.code || item.number || item.record_id || index + 1;
      }
      if (type === 'question') {
        return item.number || item.code || item.display_code || item.question_no || index + 1;
      }
      return item.number || item.question_no || item.code || item.display_code || index + 1;
    },
    tabLabel(tab) {
      if (!tab) return '';
      if (tab.key === 'questions') return `${tab.label} (${this.questions.length})`;
      if (tab.key === 'linked-notes') return `${tab.label} (${this.linkedNotes.length})`;
      if (tab.key === 'mocks') return `${tab.label} (${this.mockQuestions.length})`;
      return tab.label;
    },
    closePreview() {
      this.previewOpen = false;
      this.previewKind = '';
      this.previewItem = null;
      this.previewSrc = '';
      this.previewError = '';
    },
    openPreview(kind, item) {
      if (kind === 'note' && this.sectionLocks.notes) return;
      if (kind === 'mock' && this.sectionLocks.mocks) return;
      if (kind === 'question' && this.sectionLocks.questions) return;

      if (kind === 'mock') {

        if (item.score == null) {

          this.$toast.error("Please Attempt Mock First to Review!")
          return;
        }

      }
      this.previewKind = kind;
      this.previewItem = item;
      this.previewError = '';
      this.previewSrc = this.buildPreviewSrc(kind, item);
      if (!this.previewSrc) {
        this.previewError = `Could not open this ${kind}.`;
      }
      this.previewOpen = true;
    },
    buildPreviewSrc(kind, item) {
      if (!item) return '';
      if (kind === 'note') {
        if (item.record_id == null) return '';
        const type = item.type === 'subnotes' ? 'subnotes' : 'notes';
        localStorage.setItem('notestype', type);
        localStorage.setItem('notestitle', item.label || item.display_code || 'Notes');
        return this.resolveHref({ path: '/notespage', query: { id: String(item.record_id), type, embeddedPreview: '1' } });
      }
      if (kind === 'mock') {
        if (item.mock_id == null) return '';
        
        localStorage.setItem('question', 'normal');
        localStorage.setItem('mockObject', JSON.stringify({
          id: item.mock_id,
          title: item.mock_title || '',
          duration: item.mock_duration || 0,
        }));
        localStorage.setItem('mockStartQuestionId', String(item.id));
        return this.resolveHref({
          name: 'MockQuestionPreview',
          params: { id: item.mock_id },
          query: { embeddedPreview: '1', startQuestionId: String(item.id) },
        });
      }
      const questionTarget = this.linkedQuestionTarget(item);
      if (!questionTarget.entityId) return '';
      localStorage.setItem('questiontitle', questionTarget.title);
      const query = { embeddedPreview: '1' };
      const linkedCodes = (this.questions || []).map((q) => q.code).filter(Boolean);
      if (linkedCodes.length) {
        query.linkedCodes = linkedCodes.join(',');
      }
      if (this.variant === 'links' && this.title) {
        query.linkedFromCode = String(this.title);
      }
      if (item.code) {
        query.startCode = item.code;
      }
      const parent = this.currentQuestion;
      if (parent && this.variant === 'links') {
        const parentTopic = (this.panelHeaderLabel('questions') || '').trim();
        if (parentTopic) query.previewParentTopic = parentTopic;
        if (parent.subject_id != null) query.previewParentSubjectId = String(parent.subject_id);
        if (parent.condition_id != null) query.previewParentConditionId = String(parent.condition_id);
        if (parent.sublist_id != null) query.previewParentSublistId = String(parent.sublist_id);
        if (parent.presentation_id != null) query.previewParentPresentationId = String(parent.presentation_id);
      }
      return this.resolveHref({
        name: 'QuestionsPage',
        params: { id: questionTarget.entityId },
        query,
      });
    },
    linkedQuestionTarget(que) {
      if (que.sublist_id) return { title: 'Sublist', entityId: que.sublist_id };
      if (que.condition_id) return { title: 'Conditions', entityId: que.condition_id };
      if (que.presentation_id) return { title: 'Presentations', entityId: que.presentation_id };
      return { title: 'Chapter', entityId: que.subject_id };
    },
    resolveHref(location) {
      if (!this.$router || typeof this.$router.resolve !== 'function') return '';
      const resolved = this.$router.resolve(location);
      return resolved && resolved.href ? resolved.href : '';
    },
  },
};
</script>

<style scoped>
.nlq-sec {
  padding: 0 0 30px 0;
}

/* ── Tab strip ── */
.nlq-tabs {
  display: flex;
  gap: 16px;
  margin-bottom: 10px;
}

.nlq-tab {
  flex: 1;
  cursor: pointer;
}

.nlq-tab.is-locked {
  cursor: not-allowed;
}

.nlq-tab-header {
  border-radius: 40px;
  padding: 0;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  color: white;
  transition: background 0.3s ease;
  gap: 0;
  overflow: hidden;
  min-height: 42px;
}

.nlq-tab-header--locked {
  padding: 0;
}

.nlq-tab-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding: 10px 13px;
}

.nlq-tab-label {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.nlq-tab-leading-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
}

.nlq-tab-leading-icon--journal {
  /* source asset is white pen on black — knock out black on grey tabs */
  mix-blend-mode: screen;
  width: 18px;
  height: 18px;
}

.nlq-tab-header h4 {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nlq-tab-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.nlq-tab-icon svg {
  width: 20px;
  height: 20px;
  margin-top: 1px;
}

/* ── Sliding panel ── */
.nlq-panel {
  border: 1px solid;
  border-top: 1px solid;
  background: white;
  padding: 20px;
  border-radius: 20px;
  margin-top: 15px;
  text-align: left;
}

.nlq-slide-enter-active,
.nlq-slide-leave-active {
  transition: all 0.3s ease;
}
.nlq-slide-enter,
.nlq-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ── Bird's-eye card ── */
.nlq-birdeye-card {
  border: 2px solid;
  border-radius: 20px;
  overflow: hidden;
  min-height: 155px;
}

.nlq-birdeye-title {
  color: #fff;
  font-weight: bold;
  font-size: 16px;
  padding: 11.5px 16px;
  text-align: center;
}

.nlq-tab-body {
  padding: 12px 14px 16px;
}

/* ── Question / mock grid (mirrors QuestionBirdsEyeView) ── */
.nlq-birdeye-grid {
  display: grid;
  grid-template-columns: repeat(20, 1fr);
  grid-column-gap: 4px;
  grid-row-gap: 8px;
  padding: 16px 12px 8px;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;
}

.nlq-birdeye-grid::-webkit-scrollbar { height: 6px; }
.nlq-birdeye-grid::-webkit-scrollbar-thumb {
  background-color: #bbb;
  border-radius: 4px;
}

.nlq-birdeye-box {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 41.95px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #000;
  background: #f1f2f2;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}

.nlq-birdeye-box:hover {
  transform: scale(1.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.nlq-birdeye-box--note {
  background: #fbad18a8;
  font-size: 12px;
}

.nlq-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
}

.nlq-preview-modal {
  width: min(1380px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
}

.nlq-preview-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-top: 5px solid;
  border-bottom: 1px solid #e6e6e6;
}

.nlq-preview-type {
  margin: 0 0 4px;
  color: #6d6e71;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.nlq-preview-header h3 {
  margin: 0;
  color: #231f20;
  font-size: 20px;
  line-height: 1.25;
}

.nlq-preview-close {
  width: 34px;
  height: 34px;
  border: 1px solid #d6d6d6;
  border-radius: 50%;
  background: #fff;
  color: #6d6e71;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nlq-preview-close:hover {
  color: #231f20;
  background: #f6f6f6;
}

.nlq-preview-body {
  padding: 18px;
  overflow-y: auto;
}

.nlq-preview-frame-wrap {
  height: min(78vh, 820px);
  min-height: 520px;
  overflow: hidden;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background: #fff;
}

.nlq-preview-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
   
}

.nlq-preview-click-blocker {
  position: absolute;
  inset: 0;
  z-index: 10;
  cursor: not-allowed;
}

.nlq-modal-fade-enter-active,
.nlq-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.nlq-modal-fade-enter,
.nlq-modal-fade-leave-to {
  opacity: 0;
}

/* ── Legend ── */
.nlq-legend {
  display: flex;
  gap: 20px;
  padding: 10px 16px 14px;
  flex-wrap: wrap;
}

.nlq-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
}

.nlq-legend-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  flex-shrink: 0;
}

/* ── Empty / loading state ── */
.nlq-state {
  text-align: center;
  padding: 30px 20px;
  color: #6d6e71;
  font-size: 14px;
  font-style: italic;
}

.nlq-state p { margin: 0; }

/* ── Journal tab ── */
.nlq-journal-panel {
  text-align: left;
}

.nlq-journal-hint {
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #555;
  font-style: normal;
}

.nlq-journal-editor-wrap {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.nlq-journal-editor-wrap ::v-deep .quill-editor-full {
  height: auto;
  min-height: 200px;
}

.nlq-journal-editor-wrap ::v-deep .ql-container {
  min-height: 180px;
  height: 220px;
  font-size: 15px;
}

.nlq-journal-editor-wrap ::v-deep .ql-editor {
  min-height: 180px;
}

.nlq-journal-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.nlq-journal-save {
  background: #fbad18;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.nlq-journal-save:hover {
  filter: brightness(0.95);
}

.nlq-journal-saved-msg {
  font-size: 13px;
  color: #2e7d32;
}

/* ── Comments tab ── */
.nlq-comments-panel {
  text-align: left;
}

.nlq-comments-list {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  max-height: 280px;
  overflow-y: auto;
}

.nlq-comment-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.nlq-user-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.nlq-comment-header {
  margin-bottom: 4px;
}

.nlq-user-name {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.nlq-comment-text {
  margin: 0;
  font-size: 14px;
  color: #555;
  line-height: 1.4;
}

.nlq-comment-input-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}

.nlq-comment-input-container input {
  flex: 1;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
}

.nlq-send-button {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
}

.nlq-send-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .nlq-tabs { gap: 8px; }

  .nlq-tab-header h4 { font-size: 11px; }

  .nlq-birdeye-grid {
    grid-template-columns: repeat(10, 1fr);
  }

  .nlq-birdeye-box {
    height: 41.95px;
    font-size: 12px;
  }

  .nlq-preview-overlay {
    padding: 12px;
    align-items: flex-start;
  }

  .nlq-preview-modal {
    max-height: calc(100vh - 24px);
  }

  .nlq-preview-header h3 {
    font-size: 17px;
  }

  .nlq-preview-body {
    padding: 10px;
  }

  .nlq-preview-frame-wrap {
    height: calc(100vh - 120px);
    min-height: 0;
  }
}
</style>
